import React, {
  Component,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { AlertDialog, InformationBox } from '@crabs-ai/ds-react';
import { deleteItems } from '../state/actions/delete-items';
import {
  useItems,
  useLinkingEnabled,
  useTracks,
  useTransitions,
  useWriteContext,
  useAssets,
} from '../utils/use-context';
import { AudioInspector } from './audio-inspector';
import { BlurInspector } from './blur-inspector';
import { CaptionsInspector } from './captions-inspector';
import { CodeInspector } from './code-inspector';
import { GifInspector } from './gif-inspector';
import { ImgInspector } from './img-inspector';
import { PdfInspector } from './pdf-inspector';
import { SolidInspector } from './solid-inspector';
import { TextInspector } from './text-inspector';
import { TransitionInspector } from './transition-inspector';
import { VideoInspector } from './video-inspector';
import { WebpInspector } from './webp-inspector';
import { AlignmentControls } from './controls/alignment-controls';
import { PlaybackRateControls } from './controls/playback-rate-controls';
import { useTranslations } from 'next-intl';
import { SettingPanel } from '@/features/brand-template/ui/panels';
import {
  ArticleFill,
  PhotoFill,
  ClosedCaptionFill,
  ContentCut,
  VolumeUpFill,
  CropSquare,
  BlurOn,
  Title,
  Select,
  TransitionChopFill,
} from '@crabs-ai/web-icons';
import { getAssetFromItem } from '../assets/utils';
import { CaptionAsset } from '../assets/assets';
import { CollapseExpandButton } from './collapse-expand-button';
import { GroupItemInspector } from './group-item-inspector';
import { isAuthoringWritingGuideCodeItem } from '../items/code/code-item-type';
import { TimingSection } from '@waka/compat/timing-section';

const TYPES_WITH_INLINE_TIMING = new Set([
  'image',
  'text',
  'solid',
  'blur',
  'pdf',
  'ppt',
]);

interface InspectorErrorBoundaryProps {
  itemIds: string[];
  children: React.ReactNode;
  onDeleteItems: () => void;
}

interface InspectorErrorBoundaryState {
  hasError: boolean;
  showDeleteDialog: boolean;
}

class InspectorErrorBoundary extends Component<
  InspectorErrorBoundaryProps,
  InspectorErrorBoundaryState
> {
  constructor(props: InspectorErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, showDeleteDialog: false };
  }

  static getDerivedStateFromError(): InspectorErrorBoundaryState {
    return { hasError: true, showDeleteDialog: true };
  }

  handleConfirmDelete = () => {
    this.props.onDeleteItems();
    this.setState({ hasError: false, showDeleteDialog: false });
  };

  handleCancel = () => {
    this.setState({ showDeleteDialog: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <AlertDialog
          open={this.state.showDeleteDialog}
          setOpen={(open) => this.setState({ showDeleteDialog: open })}
          titleNode="문제가 발생했습니다"
          descriptionNode="문제의 원인이 된 콘텐츠를 삭제해야 진행 가능합니다. 삭제하시겠습니까?"
          cancelText="취소"
          onCancel={this.handleCancel}
          confirmText="삭제"
          onConfirm={this.handleConfirmDelete}
        />
      );
    }

    return this.props.children;
  }
}

const MixedSelectionNotice = ({ alignItemId }: { alignItemId?: string }) => {
  const t = useTranslations();
  return (
    <SettingPanel icon={<Select />} title={t('Editor.inspector.multi-select')}>
      <div className="px-md py-4xs">
        <InformationBox
          type="negative"
          form="container"
          title={t('Editor.inspector.error.mixed-type-title')}
          description={t('Editor.inspector.error.mixed-type-description')}
        />
      </div>
      {/* 혼합 타입이라도 캔버스 정렬은 선택 전체에 일괄 적용 가능 */}
      {alignItemId != null && (
        <div className="border-t border-sys-style-neutral-light px-md py-2xs">
          <AlignmentControls itemId={alignItemId} />
        </div>
      )}
    </SettingPanel>
  );
};

// 영상+음성 등 재생 속도가 있는 타입만 혼합 선택된 경우:
// 혼합 선택 안내(공통 속성 편집 불가)는 그대로 두되, 재생 속도 조절만 예외로 노출한다.
const MixedSelectionRateNotice = ({
  playbackRate,
  itemId,
}: {
  playbackRate: number;
  itemId: string;
}) => {
  const t = useTranslations();
  return (
    <SettingPanel icon={<Select />} title={t('Editor.inspector.multi-select')}>
      <div className="px-md py-4xs">
        <InformationBox
          type="negative"
          form="container"
          title={t('Editor.inspector.error.mixed-type-title')}
          description={t('Editor.inspector.error.mixed-type-description')}
        />
      </div>
      <div className="border-t border-sys-style-neutral-light px-md py-2xs">
        <div className="pb-2xs text-caption-medium text-sys-cont-neutral-light">
          {t('Editor.inspector.playback-speed')}
        </div>
        <PlaybackRateControls playbackRate={playbackRate} itemId={itemId} />
      </div>
      {/* 혼합 타입이라도 캔버스 정렬은 선택 전체에 일괄 적용 가능 */}
      <div className="border-t border-sys-style-neutral-light px-md py-2xs">
        <AlignmentControls itemId={itemId} />
      </div>
    </SettingPanel>
  );
};

const CACHE_MAX_SIZE = 100;
const isAllCollapsedCache = new Map<string, boolean>();

const InspectorContentInner: React.FC<{
  itemIds: string[];
}> = ({ itemIds }) => {
  const t = useTranslations();
  const items = useItems(itemIds);
  const { assets } = useAssets();
  const { tracks } = useTracks();
  const { transitions } = useTransitions();
  const isMulti = itemIds.length > 1;

  const primaryItemId = items[0]?.id ?? null;

  const [isAllCollapsed, setIsAllCollapsed] = useState(() =>
    primaryItemId ? (isAllCollapsedCache.get(primaryItemId) ?? false) : false,
  );
  const [collapseAllTrigger, setCollapseAllTrigger] = useState(0);
  const [expandAllTrigger, setExpandAllTrigger] = useState(0);

  // 아이템 전환 시 캐시에서 isAllCollapsed 복원
  const prevPrimaryItemIdRef = useRef(primaryItemId);
  useEffect(() => {
    if (prevPrimaryItemIdRef.current !== primaryItemId) {
      prevPrimaryItemIdRef.current = primaryItemId;
      setIsAllCollapsed(
        primaryItemId
          ? (isAllCollapsedCache.get(primaryItemId) ?? false)
          : false,
      );
    }
  }, [primaryItemId]);

  // isAllCollapsed 변경 시 캐시 업데이트
  useEffect(() => {
    if (primaryItemId) {
      isAllCollapsedCache.set(primaryItemId, isAllCollapsed);
      if (isAllCollapsedCache.size > CACHE_MAX_SIZE) {
        const oldest = isAllCollapsedCache.keys().next().value;
        if (oldest !== undefined) isAllCollapsedCache.delete(oldest);
      }
    }
  }, [primaryItemId, isAllCollapsed]);

  const handleToggleAll = useCallback(() => {
    if (isAllCollapsed) {
      setExpandAllTrigger((prev) => prev + 1);
      setIsAllCollapsed(false);
    } else {
      setCollapseAllTrigger((prev) => prev + 1);
      setIsAllCollapsed(true);
    }
  }, [isAllCollapsed]);

  const collapseExpandButton = useMemo(
    () => (
      <CollapseExpandButton
        isAllCollapsed={isAllCollapsed}
        onToggle={handleToggleAll}
      />
    ),
    [isAllCollapsed, handleToggleAll],
  );

  // 선택된 항목 중 트랜지션과 일반 아이템 분리
  const transitionIds = useMemo(
    () => itemIds.filter((id) => id in transitions),
    [itemIds, transitions],
  );
  const regularItemIds = useMemo(
    () => itemIds.filter((id) => !(id in transitions)),
    [itemIds, transitions],
  );

  // isLocked는 모든 hooks 이후에 계산 (React hooks 규칙 준수)
  const isLocked = useMemo(() => {
    if (itemIds.length === 0) return false;

    return itemIds.some((itemId) => {
      const track = tracks.find((t) => t.items.includes(itemId));
      return track?.locked ?? false;
    });
  }, [itemIds, tracks]);

  // 트랜지션과 일반 아이템이 섞여있는 경우
  if (transitionIds.length > 0 && regularItemIds.length > 0) {
    return <MixedSelectionNotice />;
  }

  // 트랜지션만 선택된 경우
  if (transitionIds.length > 0) {
    // 여러 트랜지션 선택 시 첫 번째만 표시
    const firstTransitionId = transitionIds[0];
    if (firstTransitionId) {
      const selectedTransition = transitions[firstTransitionId];
      if (selectedTransition) {
        return (
          <SettingPanel
            title={t('Editor.inspector.transition.title')}
            icon={<TransitionChopFill />}
            trailingNode={collapseExpandButton}
          >
            <TransitionInspector
              transition={selectedTransition}
              collapseAllTrigger={collapseAllTrigger}
              expandAllTrigger={expandAllTrigger}
            />
          </SettingPanel>
        );
      }
    }
    return null;
  }

  if (items.length === 0) {
    return null;
  }

  const primaryItem = items[0];

  if (!primaryItem) {
    return null;
  }

  const hasMixedTypes = items.some((item) => item.type !== primaryItem.type);

  if (hasMixedTypes) {
    // 영상+음성처럼 재생 속도가 있는 타입만 섞였으면 재생 속도 조절은 예외로 허용
    const allRateCapable = items.every(
      (item) =>
        item.type === 'video' ||
        item.type === 'audio' ||
        item.type === 'gif' ||
        item.type === 'webp',
    );
    if (allRateCapable && 'playbackRate' in primaryItem) {
      return (
        <MixedSelectionRateNotice
          playbackRate={primaryItem.playbackRate}
          itemId={primaryItem.id}
        />
      );
    }
    return <MixedSelectionNotice alignItemId={primaryItem.id} />;
  }

  const renderInspector = () => {
    const primaryAsset = getAssetFromItem({ item: primaryItem, assets });

    if (primaryItem.type === 'image') {
      return (
        <SettingPanel
          title={t('Editor.inspector.image-default-setting')}
          icon={<PhotoFill />}
          trailingNode={collapseExpandButton}
        >
          <ImgInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'captions') {
      const isCaptionAssetType =
        (primaryAsset as CaptionAsset | undefined)?.clipType === 'caption';
      return (
        <SettingPanel
          title={
            isCaptionAssetType ? t('Editor.inspector.caption-setting') : ''
          }
          icon={<ClosedCaptionFill />}
          trailingNode={collapseExpandButton}
        >
          <CaptionsInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'video') {
      return (
        <SettingPanel
          title={t('Editor.inspector.clip-default-setting')}
          icon={<ContentCut />}
          trailingNode={collapseExpandButton}
        >
          <VideoInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'audio') {
      return (
        <SettingPanel
          title={t('Editor.inspector.audio-default-setting')}
          icon={<VolumeUpFill />}
          trailingNode={collapseExpandButton}
        >
          <AudioInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'text') {
      const isCaption = primaryItem.renderMeta?.clipType === 'caption';
      const titleText = isCaption
        ? t('Editor.inspector.caption-setting')
        : primaryItem.renderMeta?.clipType === 'title'
          ? t('Editor.inspector.title-setting')
          : t('Editor.inspector.text-setting');

      return (
        <SettingPanel
          title={titleText}
          icon={isCaption ? <ClosedCaptionFill /> : <Title />}
          trailingNode={collapseExpandButton}
        >
          <TextInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'solid') {
      return (
        <SettingPanel
          title={t('Editor.inspector.shape')}
          icon={<CropSquare />}
          trailingNode={collapseExpandButton}
        >
          <SolidInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'blur') {
      return (
        <SettingPanel
          title={t('Editor.inspector.blur')}
          icon={<BlurOn />}
          trailingNode={collapseExpandButton}
        >
          <BlurInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'gif') {
      return (
        <SettingPanel
          title={t('Editor.inspector.gif-default-setting')}
          icon={<PhotoFill />}
          trailingNode={collapseExpandButton}
        >
          <GifInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'webp') {
      return (
        <SettingPanel
          title={t('Editor.inspector.gif-default-setting')}
          icon={<PhotoFill />}
          trailingNode={collapseExpandButton}
        >
          <WebpInspector
            item={primaryItem}
            isMulti={isMulti}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'pdf' || primaryItem.type === 'ppt') {
      return (
        <SettingPanel
          title={t(`Editor.inspector.item-type.${primaryItem.type}`)}
          icon={<ArticleFill />}
          trailingNode={collapseExpandButton}
        >
          <PdfInspector
            item={primaryItem}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </SettingPanel>
      );
    }

    if (primaryItem.type === 'code') {
      const isWritingGuide = isAuthoringWritingGuideCodeItem(primaryItem);
      return (
        <SettingPanel
          title={isWritingGuide ? '따라쓰기 설정' : 'Code Component'}
          icon={<CropSquare />}
          trailingNode={collapseExpandButton}
        >
          <CodeInspector item={primaryItem} />
        </SettingPanel>
      );
    }

    // 트랙에 배치된 1급 GroupItem(type:'group') 전체 선택은 전용 GroupItemInspector 로
    // 멤버 목록 + 멤버 depth inspector 를 렌더한다. 멤버 inspector 는 InspectorContent
    // 재귀라 순환 import 를 피하려고 render prop 으로 주입한다. depth state 는 전역 필드
    // (inspectedGroupMemberId)라 group 전환 시 리듀서가 리셋하므로 key 리셋 트릭은 불필요.
    // (구세대 groupId 기반 그룹은 여전히 상위 inspector.tsx 의 activeGroupId 분기에서 처리한다.)
    if (primaryItem.type === 'group') {
      return (
        <GroupItemInspector
          group={primaryItem}
          renderMemberInspector={(memberItemIds) => (
            <InspectorContent itemIds={memberItemIds} />
          )}
          collapseAllTrigger={collapseAllTrigger}
          expandAllTrigger={expandAllTrigger}
        />
      );
    }

    throw new Error(
      `Unknown item type: ${JSON.stringify(primaryItem satisfies never)}`,
    );
  };

  return (
    <div className={isLocked ? 'pointer-events-none opacity-50' : ''}>
      {!TYPES_WITH_INLINE_TIMING.has(primaryItem.type) && (
        <TimingSection
          itemId={primaryItem.id}
          collapseAllTrigger={collapseAllTrigger}
          expandAllTrigger={expandAllTrigger}
        />
      )}
      {renderInspector()}
    </div>
  );
};

export const InspectorContent: React.FC<{
  itemIds: string[];
}> = ({ itemIds }) => {
  const { setState } = useWriteContext();
  const isLinkingEnabled = useLinkingEnabled();

  const handleDeleteItems = useCallback(() => {
    setState({
      update: (state) =>
        deleteItems(state, itemIds, {
          isLinkingEnabled,
        }),
      commitToUndoStack: true,
    });
  }, [itemIds, setState, isLinkingEnabled]);

  return (
    <InspectorErrorBoundary itemIds={itemIds} onDeleteItems={handleDeleteItems}>
      <InspectorContentInner itemIds={itemIds} />
    </InspectorErrorBoundary>
  );
};
